// Convert to Flying on the component record (Aaron 2026-10-09, ruling Q2 of the remove / reinstall
// lane): a Rotable (17) — or, since 2026-10-14, a Retired (18): legacy shows "Set to Flying" for
// both (parts_tab_general_item.inc:497-516) — goes back to Removed (16) so it can be installed again. Legacy's "Set to
// Flying" (mod_parts/js/parts_tab_general_item.js:25-52): a reason and a store, both required.
// The API (Aeronet.app app/items/convert_flying.py) holds legacy's guards and says whether the
// record offers it (GET /v6/items/{id}/convert-to-flying); the write needs parts_set_to_flying.
// Offered in the record header's actions menu (Header recordActions, RecordActionsKebab).
import React, { useCallback, useEffect, useState } from "react";
import { Grid, MenuItem, TextField } from "@mui/material";
import { useSnackbar } from "notistack";

import { axios } from "../../api";
import AlertDialog from "../../components/AlertDialog";
import { ActionButton, CancelButton } from "../../components/Buttons";
import { usePartsStoreOptions } from "../../hooks/usePartsStoreOptions";

export interface ConvertToFlyingValues {
  reason: string;
  store: string;
}

/** Legacy's two checks before the call (parts_tab_general_item.js:29-33). */
export function convertToFlyingErrors(values: ConvertToFlyingValues): Partial<Record<keyof ConvertToFlyingValues, string>> {
  const errors: Partial<Record<keyof ConvertToFlyingValues, string>> = {};
  if (!values.reason.trim()) errors.reason = "Please specify a reason";
  if (!values.store.trim()) errors.store = "Please specify a store";
  return errors;
}

/** Whether the record offers Convert to Flying, refetched when `refreshKey` changes. */
export function useConvertToFlyingCheck(itemId: number | undefined, refreshKey: unknown) {
  const [check, setCheck] = useState<{ allowed: boolean; reason: string | null } | null>(null);
  useEffect(() => {
    if (!itemId) return;
    let live = true;
    axios
      .get(`/v6/items/${itemId}/convert-to-flying`, { cache: false })
      .then((resp) => live && setCheck({ allowed: Boolean(resp.data?.allowed), reason: resp.data?.reason ?? null }))
      .catch(() => live && setCheck(null));
    return () => {
      live = false;
    };
  }, [itemId, refreshKey]);
  return check;
}

interface DialogProps {
  open: boolean;
  itemId: number;
  /** The parts config the record carries (store list). */
  config?: { [key: string]: any };
  onClose: () => void;
  onConverted: () => void;
}

export const ConvertToFlyingDialog: React.FC<DialogProps> = ({ open, itemId, config, onClose, onConverted }) => {
  const { enqueueSnackbar } = useSnackbar();
  const storeOptions = usePartsStoreOptions(config);
  const [values, setValues] = useState<ConvertToFlyingValues>({ reason: "", store: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof ConvertToFlyingValues, string>>>({});
  const [busy, setBusy] = useState(false);

  const convert = useCallback(() => {
    const found = convertToFlyingErrors(values);
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    axios
      .post(`/v6/items/${itemId}/convert-to-flying`, { reason: values.reason.trim(), store: values.store }, { cache: false })
      .then(() => {
        enqueueSnackbar("Converted to flying: the component can be installed", { variant: "success" });
        setValues({ reason: "", store: "" });
        onConverted();
      })
      .catch((error) => {
        const detail = error?.response?.data?.detail;
        enqueueSnackbar(typeof detail === "string" ? detail : "The component was NOT converted", { variant: "error" });
      })
      .finally(() => setBusy(false));
  }, [enqueueSnackbar, itemId, onConverted, values]);

  return (
    <AlertDialog
      open={open}
      title="Convert this component to flying?"
      handleClose={onClose}
      content={
        <Grid container spacing={2} className="mui-form" sx={{ pt: 1 }} data-testid="convert-to-flying-form">
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              minRows={2}
              label="Reason"
              value={values.reason}
              onChange={(e) => setValues((v) => ({ ...v, reason: e.target.value }))}
              error={Boolean(errors.reason)}
              helperText={errors.reason}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              required
              label="Store"
              value={values.store}
              onChange={(e) => setValues((v) => ({ ...v, store: e.target.value }))}
              error={Boolean(errors.store)}
              helperText={errors.store}
            >
              {(storeOptions ?? []).map((o: any) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      }
    >
      <CancelButton onClick={onClose} />
      <ActionButton onClick={busy ? () => {} : convert}>Convert</ActionButton>
    </AlertDialog>
  );
};
